import { ejsRoms, rememberedSystem, rememberSystem } from "../emulator/ejs";
import { ejsStates } from "../emulator/ejsStates";
import { isSaveFileFor, romBaseName } from "../emulator/saves";
import { slotStore } from "../emulator/slots";
import { forgetRecords, loadRecords, saveRecords } from "../emulator/syncRecords";
import type { CloudRom } from "./cloudLibrary";
import { moveIdbfs, readIdbfs } from "./idbfs";
import type { LocalGame } from "./localLibrary";

// An admin can rename a library game. Its id (the file's SHA-256) stays,
// and the server moves everyone's cloud saves; this brings a browser's own
// copy along: the game file, its saves and states, slot names and stats,
// and what cloud sync remembers, all keyed by file name.

/** A save or state of `from` under the game's new name `to`, or null for files of other games. */
export function renamedFile(from: string, to: string, file: string): string | null {
  return isSaveFileFor(from, file) ? romBaseName(to) + file.slice(romBaseName(from).length) : null;
}

async function sha256Hex(bytes: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function localBytes(game: LocalGame): Promise<Uint8Array | null> {
  const blob = await ejsRoms.get(game.name).catch(() => undefined);
  if (blob) return new Uint8Array(await blob.arrayBuffer());
  const path = `/data/games/${game.name}`;
  return (await readIdbfs("/data", path)).find((f) => f.path === path)?.bytes ?? null;
}

async function renameLocal(from: string, to: string) {
  if (await ejsRoms.get(from).catch(() => undefined)) {
    const blob = (await ejsRoms.get(from))!;
    await ejsRoms.put(to, blob);
    await ejsRoms.remove(from);
    const system = rememberedSystem(from);
    if (system) rememberSystem(to, system);
    for (const file of await ejsStates.list()) {
      const name = renamedFile(from, to, file);
      if (name) await ejsStates.move(file, name);
    }
  } else {
    const saves = [...(await readIdbfs("/data", "/data/saves/")), ...(await readIdbfs("/data", "/data/states/"))];
    const moves: [string, string][] = [[`/data/games/${from}`, `/data/games/${to}`]];
    for (const save of saves) {
      const name = renamedFile(from, to, save.name);
      if (name) moves.push([save.path, save.path.slice(0, -save.name.length) + name]);
    }
    await moveIdbfs("/data", moves);
  }
  slotStore.move(from, to);
  const records = loadRecords(from);
  if (Object.keys(records).length && !Object.keys(loadRecords(to)).length) {
    saveRecords(
      to,
      Object.fromEntries(
        Object.entries(records).map(([file, record]) => [renamedFile(from, to, file) ?? file, record]),
      ),
    );
    forgetRecords(from);
  }
}

/**
 * Renames local games that are a library game under its old name: same
 * size and contents as a library entry no local game is named after.
 * Resolves true if anything moved.
 */
export async function followRenames(local: LocalGame[], cloud: CloudRom[]): Promise<boolean> {
  const localBases = new Set(local.map((game) => romBaseName(game.name)));
  const cloudBases = new Set(cloud.map((rom) => romBaseName(rom.name)));
  const renamed = cloud.filter((rom) => !localBases.has(romBaseName(rom.name)));
  const stale = local.filter((game) => !cloudBases.has(romBaseName(game.name)));
  if (!renamed.length || !stale.length) return false;
  let moved = false;
  for (const game of stale) {
    const bytes = await localBytes(game);
    if (!bytes) continue;
    const sameSize = renamed.filter((rom) => rom.size === bytes.length);
    if (!sameSize.length) continue;
    const id = await sha256Hex(bytes as BufferSource);
    const rom = sameSize.find((candidate) => candidate.id === id);
    if (!rom) continue;
    await renameLocal(game.name, rom.name);
    moved = true;
  }
  return moved;
}
